// App-side delegate for `@heidi/session/data` (ARCFE-1003) — the infinite
// (v2) list variant. Same split as the v1 delegate beside it: the package
// owns key/fetch/paging/retry, this file owns the review-context composition.
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getSessionsByTeamMemberId } from '@/pages/api/scribeAPIV2';
import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
  SESSIONS_PAGE_SIZE,
  SESSIONS_QUERY_RETRY_DELAY,
} from '@heidi/session/contracts';
import {
  sessionQueryVersionAtom,
  useQueryAdminSessionsV2,
  useQueryTeamMemberSessionsV2,
} from '@heidi/session/data';
import {
  selectedReviewMemberAtom,
  useCanReviewOtherUsersSessionList,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useIsReviewingOtherUser, useQueryUser } from './useQueryUser';

export { GET_INFINITE_SESSIONS, GET_INFINITE_TEAM_MEMBER_SESSIONS };

export function useGetPageSize() {
  return SESSIONS_PAGE_SIZE;
}

function useSessionListReviewContext() {
  const { teamDetails, isSupportRole } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const hasAllowlistedSessionReviewAccess = useCanReviewOtherUsersSessionList();
  return {
    teamId: teamDetails?.team.id,
    memberId: selectedReviewUser ?? undefined,
    canReviewOtherUsersSessionList: isSupportRole || hasAllowlistedSessionReviewAccess,
  };
}

export function useQuerySessionsV2(enabled = true) {
  const { teamId, memberId, canReviewOtherUsersSessionList } =
    useSessionListReviewContext();
  const teamMemberSessions = useQueryTeamMemberSessions(enabled);
  const adminSessions = useQueryAdminSessions(enabled);
  const { data: kindeUserId } = useQueryUser((data) => data?.kinde_user_id);

  if (teamId && memberId && memberId !== kindeUserId && canReviewOtherUsersSessionList) {
    return teamMemberSessions;
  }
  return adminSessions;
}

export function useQueryAdminSessions(enabled = true) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: user } = useQueryUser((data) => data?.kinde_user_id);
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isDesktopOnlineReady = useIsDesktopOnlineReady();

  return useQueryAdminSessionsV2({
    pageSize: useGetPageSize(),
    retryDelay: SESSIONS_QUERY_RETRY_DELAY,
    enabled: Boolean(
      isAuthenticated &&
      !!user &&
      isDesktopOnlineReady &&
      sessionQueryVersion === 'v2' &&
      enabled
    ),
  });
}

export function useQueryTeamMemberSessions(enabled = true) {
  const { teamId, memberId, canReviewOtherUsersSessionList } =
    useSessionListReviewContext();
  const { isAuthenticated } = useIsAuthenticated();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isDesktopOnlineReady = useIsDesktopOnlineReady();

  return useQueryTeamMemberSessionsV2({
    teamId,
    memberId,
    pageSize: useGetPageSize(),
    fetchTeamMemberSessions: getSessionsByTeamMemberId,
    retryDelay: SESSIONS_QUERY_RETRY_DELAY,
    enabled:
      isAuthenticated &&
      !!canReviewOtherUsersSessionList &&
      !!isReviewingOtherUser &&
      isDesktopOnlineReady &&
      sessionQueryVersion === 'v2' &&
      enabled,
  });
}
